import SublistsSvg from '@/assets/svgs/contact-lists/sublists.svg';
import ActiveIcon from '@/assets/svgs/statuses/active.svg';
import AwaitingIcon from '@/assets/svgs/statuses/awaiting.svg';
import CompleteIcon from '@/assets/svgs/statuses/complete.svg';
import DeferredIcon from '@/assets/svgs/statuses/deferred.svg';
import PausedIcon from '@/assets/svgs/statuses/paused.svg';
import PendingIcon from '@/assets/svgs/statuses/pending.svg';
import { AbbreviatedNumber } from '@/shared/components/AbbreviatedNumber';
import DetailsCard from '@/shared/components/DetailsCard';

interface Sublist {
  sublist_id: number;
  timezone: string;
  leads_remaining: number;
  sublist_state: string;
}

interface SublistsCardProps {
  sublists: Sublist[];
  selectedSublists: Set<number>;
  selectAllSublists: boolean;
  isSelectAllIndeterminate: boolean;
  onSelectSublist: (sublistId: number, selected: boolean) => void;
  onSelectAllSublists: (selected: boolean) => void;
  leadsLoaded: number;
  leadsRemaining: number;
}

const getStateIcon = (state?: string, leadsLoaded?: number, leadsRemaining?: number) => {
  switch (state) {
    case 'ACTIVE':
      return leadsLoaded && leadsLoaded > 0 && leadsRemaining === 0 ? (
        <CompleteIcon className="h-4 w-4" />
      ) : (
        <ActiveIcon className="h-4 w-4" />
      );
    case 'PAUSED':
      return <PausedIcon className="h-4 w-4" />;
    case 'PENDING':
      return <PendingIcon className="h-4 w-4" />;
    case 'AWAITING-CHECK':
      return <AwaitingIcon className="h-4 w-4" />;
    case 'DEFERRED':
      return <DeferredIcon className="h-4 w-4" />;
    default:
      return null;
  }
};

export const SublistsCard = ({
  sublists,
  selectedSublists,
  selectAllSublists,
  isSelectAllIndeterminate,
  onSelectSublist,
  onSelectAllSublists,
  leadsLoaded,
  leadsRemaining,
}: SublistsCardProps) => {
  return (
    <div>
      <DetailsCard icon={<SublistsSvg />} title={`Sublists (${sublists.length || 0})`}>
        {sublists && sublists.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="w-12">
                    <input
                      type="checkbox"
                      className="checkbox-primary checkbox checkbox-sm"
                      checked={selectAllSublists}
                      ref={(el) => {
                        if (el) el.indeterminate = isSelectAllIndeterminate;
                      }}
                      onChange={(e) => onSelectAllSublists(e.target.checked)}
                    />
                  </th>
                  <th>Timezone</th>
                  <th>Remaining</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sublists.map((sublist) => (
                  <tr key={sublist.sublist_id} className="hover">
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox-primary checkbox checkbox-sm"
                        checked={selectedSublists.has(sublist.sublist_id)}
                        onChange={(e) =>
                          onSelectSublist(sublist.sublist_id, e.target.checked)
                        }
                      />
                    </td>
                    <td>{sublist.timezone}</td>
                    <td>
                      <AbbreviatedNumber value={sublist.leads_remaining} />
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {getStateIcon(sublist.sublist_state, leadsLoaded, leadsRemaining)}
                        <span>{sublist.sublist_state}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Bulk Actions */}
            {selectedSublists.size > 0 && (
              <div className="dark:border-base-200 dark:bg-base-200 rounded-lg border-t border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedSublists.size} sublist
                    {selectedSublists.size === 1 ? '' : 's'} selected
                  </span>
                  <div className="flex gap-2">
                    <button className="btn btn-outline btn-sm">Export Selected</button>
                    <button className="btn btn-outline btn-error btn-sm">
                      Delete Selected
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">No sublists found</div>
        )}
      </DetailsCard>
    </div>
  );
};
